import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase, Profile, UserRole } from "@/lib/supabase";

// Import API service functions
const API_BASE = '/api';

// API service functions
async function initiateRegistration(
    email: string,
    fullName: string,
    accountType: 'admin' | 'staff' | 'teacher' | 'guardian',
    password: string
): Promise<{ success: boolean; message: string; error?: string }> {
    const response = await fetch(`${API_BASE}/register`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            action: 'initiate-registration',
            email,
            fullName,
            accountType,
            password,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
    }

    return response.json();
}

async function verifyCode(email: string, code: string): Promise<{ success: boolean; message: string; error?: string }> {
    const response = await fetch(`${API_BASE}/verify`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            code,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Verification failed');
    }

    return response.json();
}

async function resendCode(email: string): Promise<{ success: boolean; message: string; error?: string }> {
    const response = await fetch(`${API_BASE}/resend`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
        }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to resend code');
    }

    return response.json();
}

// ============================================================
// OAUTH REDIRECT ERROR PARSING
// ============================================================
// When the "before-user-created" Postgres hook rejects an OAuth sign-in
// (see supabase/sql/oauth_gate_hook.sql), Supabase completes the OAuth
// redirect back to this app with the failure encoded in the URL — either
// as a query string or a hash fragment, depending on flow type — rather
// than as a normal JS promise rejection from signInWithOAuth(). This
// helper extracts that message once, on load, and cleans the URL.
function extractAndClearOAuthError(): string | null {
    if (typeof window === "undefined") return null;

    const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
    const query = window.location.search.startsWith("?")
        ? window.location.search.slice(1)
        : window.location.search;

    const hashParams = new URLSearchParams(hash);
    const queryParams = new URLSearchParams(query);

    const description =
        hashParams.get("error_description") ||
        queryParams.get("error_description") ||
        hashParams.get("error") ||
        queryParams.get("error");

    if (!description) return null;

    // Clean the error params out of the URL without a reload, so a
    // refresh doesn't keep re-showing the same stale error.
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    return decodeURIComponent(description.replace(/\+/g, " "));
}

// Auth Context Interface
interface AuthContextType {
    session: Session | null;
    user: User | null;
    profile: Profile | null;
    loading: boolean;
    authError: string | null;
    clearAuthError: () => void;
    signIn: (email: string, password: string) => Promise<{ error: string | null }>;
    signInWithGoogle: () => Promise<{ error: string | null }>;
    signInWithFacebook: () => Promise<{ error: string | null }>;
    signOut: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    changePassword: (newPassword: string) => Promise<{ error: string | null }>;
    // New registration methods
    register: (email: string, fullName: string, accountType: 'admin' | 'staff' | 'teacher' | 'guardian', password: string) => Promise<{ error: string | null; message?: string }>;
    verify: (email: string, code: string) => Promise<{ error: string | null; message?: string }>;
    resend: (email: string) => Promise<{ error: string | null; message?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);

    const clearAuthError = useCallback(() => setAuthError(null), []);

    const fetchProfile = useCallback(async (userId: string) => {
        try {
            const { data, error } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", userId)
                .maybeSingle();

            if (error) {
                console.error("Profile fetch error:", error);
                return;
            }
            setProfile(data as Profile | null);
        } catch (error) {
            console.error("Profile fetch exception:", error);
        }
    }, []);

    // Catch a rejected-OAuth-signup redirect once on initial load, before
    // the session listener below runs. Even though the "before user
    // created" hook prevents the account from being created server-side,
    // we still surface a clear message instead of leaving the person on
    // a silently-stuck login screen.
    useEffect(() => {
        const message = extractAndClearOAuthError();
        if (message) {
            setAuthError(message);
        }
    }, []);

    useEffect(() => {
        // Check if Supabase is configured
        if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
            console.error("❌ Supabase is not configured properly!");
            console.error("VITE_SUPABASE_URL:", import.meta.env.VITE_SUPABASE_URL ? "✅ Set" : "❌ Missing");
            console.error("VITE_SUPABASE_ANON_KEY:", import.meta.env.VITE_SUPABASE_ANON_KEY ? "✅ Set" : "❌ Missing");
            setLoading(false);
            return;
        }

        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                fetchProfile(session.user.id).finally(() => setLoading(false));
            } else {
                setLoading(false);
            }
        });

        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                (async () => {
                    await fetchProfile(session.user.id);
                    setLoading(false);
                })();
            } else {
                setProfile(null);
                setLoading(false);
            }
        });

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, [fetchProfile]);

    const refreshProfile = useCallback(async () => {
        if (user) await fetchProfile(user.id);
    }, [user, fetchProfile]);

    const signIn = useCallback(async (email: string, password: string) => {
        try {
            setAuthError(null);
            const { error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) return { error: error.message };
            return { error: null };
        } catch (error) {
            console.error("Sign in error:", error);
            return { error: error instanceof Error ? error.message : "Sign in failed" };
        }
    }, []);

    const signInWithGoogle = useCallback(async () => {
        try {
            setAuthError(null);
            const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: { redirectTo: window.location.origin },
            });
            if (error) return { error: error.message };
            return { error: null };
        } catch (error) {
            console.error("Google sign in error:", error);
            return { error: error instanceof Error ? error.message : "Google sign in failed" };
        }
    }, []);

    const signInWithFacebook = useCallback(async () => {
        try {
            setAuthError(null);
            const { error } = await supabase.auth.signInWithOAuth({
                provider: "facebook",
                options: { redirectTo: window.location.origin },
            });
            if (error) return { error: error.message };
            return { error: null };
        } catch (error) {
            console.error("Facebook sign in error:", error);
            return { error: error instanceof Error ? error.message : "Facebook sign in failed" };
        }
    }, []);

    const signOut = useCallback(async () => {
        try {
            await supabase.auth.signOut();
            setProfile(null);
            setSession(null);
            setUser(null);
        } catch (error) {
            console.error("Sign out error:", error);
        }
    }, []);

    const changePassword = useCallback(async (newPassword: string) => {
        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) return { error: error.message };

            await supabase
                .from("profiles")
                .update({ must_change_password: false })
                .eq("id", user?.id);
            
            await refreshProfile();
            return { error: null };
        } catch (error) {
            console.error("Change password error:", error);
            return { error: error instanceof Error ? error.message : "Password change failed" };
        }
    }, [user, refreshProfile]);

    // New: Register user
    const register = useCallback(async (
        email: string,
        fullName: string,
        accountType: 'admin' | 'staff' | 'teacher' | 'guardian',
        password: string
    ): Promise<{ error: string | null; message?: string }> => {
        try {
            console.log("📝 Registering user:", { email, fullName, accountType });
            const response = await initiateRegistration(email, fullName, accountType, password);
            console.log("✅ Registration response:", response);
            return { error: null, message: response.message };
        } catch (error) {
            console.error("❌ Registration error:", error);
            return { 
                error: error instanceof Error ? error.message : 'Registration failed',
                message: undefined
            };
        }
    }, []);

    // New: Verify code
    const verify = useCallback(async (email: string, code: string): Promise<{ error: string | null; message?: string }> => {
        try {
            console.log("🔍 Verifying code for:", email);
            const response = await verifyCode(email, code);
            console.log("✅ Verification response:", response);
            return { error: null, message: response.message };
        } catch (error) {
            console.error("❌ Verification error:", error);
            return { 
                error: error instanceof Error ? error.message : 'Verification failed',
                message: undefined
            };
        }
    }, []);

    // New: Resend code
    const resend = useCallback(async (email: string): Promise<{ error: string | null; message?: string }> => {
        try {
            console.log("📤 Resending code for:", email);
            const response = await resendCode(email);
            console.log("✅ Resend response:", response);
            return { error: null, message: response.message };
        } catch (error) {
            console.error("❌ Resend error:", error);
            return { 
                error: error instanceof Error ? error.message : 'Failed to resend code',
                message: undefined
            };
        }
    }, []);

    const value: AuthContextType = {
        session,
        user,
        profile,
        loading,
        authError,
        clearAuthError,
        signIn,
        signInWithGoogle,
        signInWithFacebook,
        signOut,
        refreshProfile,
        changePassword,
        register,
        verify,
        resend,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export function useRole(): { 
    isAdmin: boolean; 
    isStaff: boolean; 
    isTeacher: boolean;
    isGuardian: boolean; 
    isStaffOrAdmin: boolean; 
    role: UserRole | null 
} {
    const { profile } = useAuth();
    const role = profile?.role ?? null;
    return {
        isAdmin: role === "admin",
        isStaff: role === "staff",
        isTeacher: role === "teacher",
        isGuardian: role === "guardian",
        isStaffOrAdmin: role === "admin" || role === "staff" || role === "teacher",
        role,
    };
}
